import React, { useEffect } from 'react';
import { useTestStore } from '../store/useTestStore';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Clock, AlertCircle, Activity, Bug } from 'lucide-react';

export const TesterDashboardPage: React.FC = () => {
    const { activePlan, tickets, fetchActivePlan, fetchMyTickets, isLoading } = useTestStore();
    const { user } = useAuth();

    useEffect(() => {
        fetchActivePlan();
        if (user) {
            fetchMyTickets(user._id);
        }
    }, [user]);

    if (isLoading && !activePlan) {
        return <div className="p-8 text-center text-gray-400">Chargement du portail testeur...</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8 text-white">
            <h1 className="text-3xl font-serif text-gold mb-8">Espace Bêta-Testeur</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Active Mission */}
                <div className="lg:col-span-2 space-y-8">
                    <section className="bg-white/5 border border-white/10 rounded-xl p-6">
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <Activity className="text-gold" />
                            Mission en cours
                        </h2>
                        {activePlan ? (
                            <div>
                                <h3 className="text-2xl font-serif mb-2">{activePlan.title}</h3>
                                {/* Render RichText description if possible, or plain text */}
                                <div className="prose prose-invert mb-6 text-gray-300">
                                    {/* Simplified rendering for Slate JSON */}
                                    {JSON.stringify(activePlan.description)}
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-bold text-gray-400 uppercase text-sm">Objectifs</h4>
                                    <div className="grid gap-3">
                                        {activePlan.tasks?.map((task: any, idx: number) => (
                                            <div key={idx} className="flex items-start gap-3 bg-black/20 p-3 rounded">
                                                <input type="checkbox" className="mt-1" />
                                                <span className={task.isMandatory ? 'text-red-400' : 'text-gray-300'}>
                                                    {task.description} {task.isMandatory && '(Obligatoire)'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Aucune mission de test active.</p>
                        )}
                    </section>
                </div>

                {/* My Tickets */}
                <div className="lg:col-span-1">
                    <section className="bg-white/5 border border-white/10 rounded-xl p-6 h-full">
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <Bug className="text-gold" />
                            Mes Tickets
                        </h2>
                        <div className="space-y-3">
                            {tickets.map((ticket) => (
                                <div key={ticket.id} className="bg-black/20 p-3 rounded border-l-2 border-gold flex flex-col gap-1">
                                    <div className="flex justify-between items-start">
                                        <span className="font-bold text-sm truncate">{ticket.title}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${ticket.status === 'resolved' ? 'bg-green-500/20 text-green-300' :
                                            ticket.status === 'in-progress' ? 'bg-blue-500/20 text-blue-300' :
                                                'bg-gray-500/20 text-gray-300'
                                            }`}>
                                            {ticket.status}
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-500">{new Date(ticket.createdAt).toLocaleDateString()}</span>
                                </div>
                            ))}
                            {tickets.length === 0 && (
                                <p className="text-sm text-gray-500">Aucun ticket rapporté.</p>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};
